/**
 * TechnicianWorklistTab — PROD-2007.
 *
 * The screen for the person who runs the machine. It answers one question:
 * *what am I running today, and what do I do next?*
 *
 * THE DAY IS PICKABLE (PROD-2408). It opens on today and stays there unless
 * the technician says otherwise; the three decisions that came with the
 * picker are recorded at their code, not here: `WorklistDay` (reset to today
 * on remount, and why the state is `null` rather than a date), `canAdvance`
 * (the pipeline buttons are ABSENT off today, in both directions), and the
 * date is deliberately NOT in the URL — see the note under `onDayChange`.
 *
 * Structurally it is the Phlebotomist Order Requests tab (`order-requests-tab
 * .tsx`) applied to device sessions — one flat client-paginated table, request-
 * centric columns, and status deciding what each row offers. Self-fetching, no
 * props, mounted behind the role gate + flag in `frame-scheduling.tsx`.
 *
 * WHAT THIS SURFACE DELIBERATELY CANNOT DO
 * ----------------------------------------
 * Nothing here may END a session. There is no cancel, no no-show, no un-mark
 * and no overflow menu — a technician holds no clinical licence, and those
 * transitions belong to the front desk (Andrew, 16 Sep). They are ABSENT, not
 * disabled: a greyed-out cancel still tells the operator the power exists and
 * invites them to go find someone who has it. The consequence is the design
 * invariant for the whole screen — **a row can be wrong by being behind, never
 * by being impossible.**
 *
 * That is also why the action column is one button and not a status dropdown.
 * The next step is read out of `nextSessionStep` (`lib/session-status.ts`):
 * the pipeline's SHAPE — which statuses advance, in what order, to which code
 * — is never retyped here, so if the pipeline changes it changes there, and a
 * status this surface does not recognise silently yields no button rather than
 * a guessed one.
 *
 * Two status questions DO live in this file, and both are about what this
 * screen draws rather than about where a session goes next: `SessionActions`
 * asks which rows carry the attach affordance (the step's code is `Fulfilled`
 * — the row being worked — OR the session has already completed; Andrew,
 * 18 Sep), and `SessionStatus` tests the badge's own `booked` bucket to catch
 * the badge guessing. Neither decides a transition.
 *
 * SPLIT: container vs body
 * ------------------------
 * `TechnicianWorklistTab` is a thin hook-to-props adapter — it composes
 * `useTechnicianSessions`, resolves the wire status object and owns the
 * mutation. `TechnicianWorklistBody` is presentational plus view state (device
 * filter, page, drawer) and takes the seven-state machine as a prop. The split
 * exists because the seven states are the screen: they have to be storyable and
 * testable without standing up three queries each.
 *
 * THE ONE QUERY THE BODY OWNS: REPORTS (PROD-2374)
 * ------------------------------------------------
 * `useSessionReports` is called in the BODY, not the container, because its
 * input is the ids of the rows CURRENTLY ON SCREEN — and which rows those are
 * is the device filter and the page, i.e. view state, which lives here. Asking
 * for the whole day instead would be an unbounded request for documents nobody
 * is looking at, and the endpoint caps its batch at 50 ids.
 *
 * It is a SECOND async source over the same table, which is exactly how the
 * zero-versus-unknown contract (PROD-2364) gets broken, so the boundary is
 * absolute: **nothing derived from reports may reach the record count, the
 * pagination footer, or any of the seven states.** Those read `state` and
 * `filteredRows`, and nothing else. Reports are supplementary — the day's work
 * stands whether or not a document lookup ever answers.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  Eye,
  File as FileGlyph,
  Paperclip,
  SlidersHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { AppointmentStatusChip } from '@/components/atoms/status-badge';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import { ListPaginationFooter } from '@/shared/ui/molecules/list-pagination-footer';
import {
  parseLocalISODate,
  todayLocalISODate,
  toLocalISODate,
  useTechnicianSessions,
  type TechnicianSessionRow,
  type TechnicianWorklistState,
} from '@/scheduling/services/use-technician-sessions';
import { CalendarDateControl } from '@/components/blocks/calendar-date-control';
import { formatLongDate } from '@/shared/lib/date-format';
import {
  isBookedSessionStatus,
  isCompletedSessionStatus,
  nextSessionStep,
  type SessionStatusStep,
} from '@/scheduling/lib/session-status';
import {
  useSessionReports,
  type SessionReport,
} from '@/scheduling/services/use-session-reports';
import { useStatusList } from '@/services/scheduling/use-status-list';
import { useUpdateAppointmentStatus } from '@/scheduling/services/use-update-appointment-status';
import { NewFilePopup } from '@/documents/components/new-file-popup';
import { SESSION_REPORT_TYPES } from '@/scheduling/lib/session-report-types';
import type { AppointmentStatus, CalendarDevice } from '@/types/scheduling-api';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ROW_ICON_BUTTON } from '@/components/blocks/row-actions';

const PAGE_SIZES = [10, 20, 50] as const;
type PageSize = (typeof PAGE_SIZES)[number];

/** Sentinel for the device Segmented's "no filter" option. Not a device id. */
const ALL_DEVICES = '__all__';

/**
 * What a just-attached report is called until the fetched record arrives —
 * the name the drawer was SEEDED with (the product), because `onSaved` hands
 * back a bare document id and nothing else. See `handleReportSaved`.
 */
const SEEDED_REPORT_NAME = 'Report';

/**
 * Title AND accessible name of the attach control on a row whose patient the
 * wire did not identify — see `SessionActions`. It names the control and then
 * says why it is dead, in that order, so the two cases read as the same control
 * in two conditions rather than as two different controls.
 */
const ATTACH_NEEDS_PATIENT =
  'Attach report — unavailable: this session has no patient record';

/**
 * THE DAY THE SCREEN IS SHOWING (PROD-2408).
 *
 * `null` means "whatever today is", which is not the same thing as storing
 * today's date: it is the difference between a screen pinned to 21 September
 * and a screen that says *now*. A technician who opens the worklist at 23:50
 * and is still on it at 00:10 should be looking at the new day's sessions,
 * which is the behaviour the clock-derived version had and which a stored
 * string would silently lose. The label reads "Today" in exactly this state,
 * so the word can never be attached to a day that is no longer today.
 *
 * Picking today's date out of the calendar collapses BACK to `null` rather
 * than pinning it — otherwise the screen would look identical and behave
 * differently, which is the worst of both.
 */
type WorklistDay = string | null;

export function TechnicianWorklistTab() {
  const [pinnedDay, setPinnedDay] = useState<WorklistDay>(null);

  /**
   * DECISION — RESET, NOT REMEMBER. The day is component state, so leaving
   * the tab and coming back opens on today again.
   *
   * Two reasons. (1) The screen answers "what am I running now"; a technician
   * who steps away and returns mid-shift and is silently handed last
   * Thursday has been given a plausible, wrong answer — the failure mode this
   * whole programme keeps hitting. Today is the only default that cannot be
   * stale. (2) Every other piece of this screen's view state — the device
   * filter, the page, the page size — already resets on remount; remembering
   * only the date would need a module-level or store singleton and would make
   * the date the one sticky thing on an otherwise stateless surface.
   *
   * Within a mount the choice obviously persists: paging, filtering and
   * advancing a session all leave the chosen day alone.
   */
  const today = todayLocalISODate();
  const day = pinnedDay ?? today;
  const isToday = day === today;

  const { state, devices, rows, retry } = useTechnicianSessions(day);

  // `PUT /api/Appointment/Status` wants the whole status object, not a code.
  // The catalogue is the only place the real `display`/`definition`/`system`
  // exist, so the step's code is resolved against it exactly the way
  // `appointment-status-manager.tsx` does — sending a hand-built object would
  // put a display string on the wire that no other surface agrees with.
  const { data: statusList } = useStatusList();
  const updateStatus = useUpdateAppointmentStatus();

  // Which rows are mid-flight. Per row, not global: two technicians' sessions
  // advance independently, and freezing the whole table on one click makes a
  // busy morning feel broken. A SET, because that is what the affordance
  // already promises — `busy` disables one row's controls, so a global "one at
  // a time" bail-out meant the second row's still-enabled button swallowed its
  // own click: no mutation, no toast, no spinner, just a button that visibly
  // depressed and did nothing.
  const [advancingIds, setAdvancingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const handleAdvance = useCallback(
    (row: TechnicianSessionRow, step: SessionStatusStep) => {
      // BELT AND BRACES for the date rule (PROD-2408). `SessionActions` draws
      // no advance button off today, so nothing on the screen can reach this
      // — which is exactly why the write path should not depend on that
      // staying true. A status advance is the one irreversible thing this
      // surface does.
      if (!isToday) return;
      // Re-entrancy on the SAME row is still refused — that row's button is
      // disabled while it is in flight, so a second call for it is a duplicate
      // write, not a second technician working a second machine.
      if (advancingIds.has(row.id)) return;
      const target = statusList?.find(
        (s) => (s.code ?? '').toLowerCase() === step.code.toLowerCase(),
      );
      // No catalogue entry → no mutation. The alternative is inventing an
      // `AppointmentStatus` from the step, which would write a `display` the
      // rest of the platform does not use.
      if (!target) return;
      setAdvancingIds((prev) => new Set(prev).add(row.id));
      const settle = () =>
        setAdvancingIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      // `mutateAsync`, NOT `mutate` with an inline `onSettled`. Those inline
      // callbacks live on the hook's single shared `MutationObserver`, and its
      // `mutate()` detaches the observer from the previous mutation before
      // building the next one (`query-core/mutationObserver.ts`) — so with two
      // advances in flight the FIRST row's `onSettled` never fires and its
      // button stays disabled for the rest of the shift. The returned promise
      // settles per call, which is the only per-row signal there is.
      //
      // Both arms settle the row, and the rejection is handled here ON PURPOSE:
      // the failure is already reported by the app-wide `MutationCache.onError`
      // toast and rolled back by the hook's own `onError`, so all that is left
      // to do locally is give the row its button back.
      void updateStatus
        .mutateAsync({
          appointmentId: row.id,
          status: target as AppointmentStatus,
        })
        .then(settle, settle);
    },
    [isToday, advancingIds, statusList, updateStatus],
  );

  /**
   * DECISION — THE DAY IS NOT IN THE URL.
   *
   * It is tempting: the scheduling page puts its own `date` there, and a
   * shareable "the worklist, on the 18th" link looks free. It is not free,
   * and the reason is written at `CONFINED_STRIPPED_PARAMS` in
   * `@/app/lib/nav-confinement.ts`.
   *
   * A confined Technician lives on `/` and CANNOT leave it — the worklist is
   * their whole application. Any param this screen asserted into the URL
   * unprompted would join the exact class that produced the redirect loop
   * (442 `history.replaceState` in 12 seconds, 4,172 aborted requests): the
   * page writes it, a guard strips it, the page writes it again. `date` and
   * `view` survive today only because that constant names `actor` alone, and
   * the rule holding that line is a comment. Adding a third such param buys
   * a deep link nobody has asked for and stakes it on nobody ever widening a
   * list they are explicitly warned not to widen.
   *
   * There is also no user for the link. The technician's session is one
   * screen with no address bar in their workflow, and the date they want is
   * today — which is what an unparameterised `/` already gives them.
   *
   * Consistency seals it: the device filter, the page and the page size are
   * all component state on this screen. A URL-backed date would be the only
   * exception, and the only piece of this screen's view state that could
   * outlive the mount.
   *
   * IF THIS IS EVER REVISITED: the param must be named in the confinement
   * suite and in `nav-confinement.redirect-loop.test.tsx` — which composes
   * the guard with a URL writer and counts navigations — BEFORE it is
   * written anywhere. A unit test on the guard cannot see this defect; that
   * is the whole point of that file.
   */
  const handleDayChange = useCallback((picked: Date) => {
    const ymd = toLocalISODate(picked);
    // Collapse "today" back to the unpinned state — see `WorklistDay`.
    setPinnedDay(ymd === todayLocalISODate() ? null : ymd);
  }, []);

  return (
    <TechnicianWorklistBody
      state={state}
      devices={devices}
      rows={rows}
      onRetry={retry}
      advancingIds={advancingIds}
      onAdvance={handleAdvance}
      day={day}
      isToday={isToday}
      onDayChange={handleDayChange}
    />
  );
}

// ── Body ─────────────────────────────────────────────────────────────────

export interface TechnicianWorklistBodyProps {
  state: TechnicianWorklistState;
  /** `readonly` — the hook hands out a shared singleton in every non-ready
   *  state, so an in-place `.sort()` here would corrupt every other consumer's
   *  empty array. */
  devices: readonly CalendarDevice[];
  /** Always `[]` unless `state === 'ready'` — the hook's own contract. */
  rows: readonly TechnicianSessionRow[];
  onRetry: () => void;
  /** Session ids whose status mutation is in flight. Empty when none is. */
  advancingIds: ReadonlySet<string>;
  onAdvance: (row: TechnicianSessionRow, step: SessionStatusStep) => void;
  /** The day these rows are, as local `YYYY-MM-DD`. Drives the copy. */
  day: string;
  /**
   * Whether `day` IS the current local day. Passed rather than re-derived so
   * the header's label, the date-aware copy and the advance rule cannot
   * disagree with the hook about which day it asked for.
   */
  isToday: boolean;
  onDayChange: (date: Date) => void;
}

export function TechnicianWorklistBody({
  state,
  devices,
  rows,
  onRetry,
  advancingIds,
  onAdvance,
  day,
  isToday,
  onDayChange,
}: TechnicianWorklistBodyProps) {
  const [deviceFilter, setDeviceFilter] = useState<string>(ALL_DEVICES);
  const [pageSize, setPageSize] = useState<PageSize>(10);
  const [attachTarget, setAttachTarget] = useState<TechnicianSessionRow | null>(
    null,
  );
  /**
   * Names of reports attached during THIS page's lifetime, keyed by session id
   * — the optimistic half of the attach, and ONLY the name.
   *
   * It is not the source of the report any more (that is `useSessionReports`);
   * it exists because `onSaved` returns a bare document id with no URL, so
   * between the save landing and the refetch answering the only honest thing
   * this screen can say is "a report is attached, and it is called this". It
   * therefore renders as TEXT, never behind the eye — see `SessionActions`.
   */
  const [seededReportNames, setSeededReportNames] = useState<
    Record<string, string>
  >({});

  /**
   * The device the table is ACTUALLY narrowing by: the technician's pick while
   * that machine is still in the registry, and the all-devices sentinel the
   * moment it is not.
   *
   * `deviceFilter` is view state; `devices` is a feed. An administrator
   * archiving a machine mid-shift (`useCalendarDevices` has `staleTime:
   * 30_000` and refetches on focus) leaves the filter pointing at an id no row
   * carries any more, and every row disappears. In a two-device practice that
   * is not merely a wrong count: the Segmented itself stops rendering at
   * `devices.length > 1`, so the "switch to All devices" the filtered-empty
   * state tells the technician to use is no longer on the screen, and the tab
   * cannot be recovered without unmounting it. With three devices the control
   * survives but holds a `value` that is not among its `options`, so nothing
   * reads as selected. One clamp covers both.
   *
   * This is PROD-2364's false zero reached through VIEW state rather than
   * loading state, which is why it is reconciled here and not left to the
   * empty-state copy.
   *
   * Derived during render, NOT reconciled in an effect: an effect would commit
   * one render of the stranded screen before correcting it. The sentinel needs
   * no special case — it is not a device id, so it never matches and lands on
   * itself. The technician's raw pick is deliberately KEPT in state: if the
   * machine comes back (an un-archive, or a registry blip) their choice is
   * restored rather than silently thrown away.
   */
  const effectiveFilter = useMemo(
    () =>
      devices.some((d) => d.id === deviceFilter) ? deviceFilter : ALL_DEVICES,
    [devices, deviceFilter],
  );

  /**
   * The page number, paired with the LIST it was chosen against — ONE piece of
   * state, because a page only means anything relative to a list: page 3 of
   * HBOT-1's day is not page 3 of the practice's day, and page 3 of Tuesday is
   * not page 3 of Wednesday. Pairing them makes the reset a DERIVATION, so the
   * technician picking a device, the archived-device clamp firing, and picking
   * another day (PROD-2408) all put the table back on page 1 by the same rule,
   * with no effect, no second render, and no way for the paths to disagree.
   *
   * The DEVICE FILTER deliberately does not reset with the day: which machine
   * a technician is working is a fact about them, not about the date.
   */
  const pageScope = `${effectiveFilter}|${day}`;
  const [page, setPage] = useState<{ scope: string; number: number }>({
    scope: '',
    number: 1,
  });
  const pageNumber = page.scope === pageScope ? page.number : 1;

  const filteredRows = useMemo(
    () =>
      effectiveFilter === ALL_DEVICES
        ? rows
        : rows.filter((r) => r.deviceId === effectiveFilter),
    [rows, effectiveFilter],
  );

  const totalRecords = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  // Clamp rather than trust: a page-size change or a refetch that shortened
  // the list must not strand the user on a page that no longer exists.
  const safePage = Math.min(pageNumber, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredRows, safePage, pageSize],
  );

  /**
   * The reports for the rows ON SCREEN — a page, never the whole day. The
   * endpoint caps a batch at 50 ids, the largest page here is 50, and the
   * hook still chunks; what this side owes it is a bounded, already-rendered set.
   *
   * `pagedRows` is `[]` in every state but `'ready'` (the sessions hook's own
   * contract), so in the six other states this asks for nothing at all.
   *
   * NOTE what is NOT taken from this hook: `isPending`. The eye is gated on a
   * report that HAS a URL, so "still loading", "failed" and "there is none" all
   * collapse to the same placeholder by construction rather than through three
   * branches that could each be got wrong. A loading flag in this component
   * would be one more thing that could leak into the count.
   */
  const visibleRowIds = useMemo(() => pagedRows.map((r) => r.id), [pagedRows]);
  const { reports, refetch: refetchReports } = useSessionReports(visibleRowIds);

  /**
   * The device Segmented does NOT toggle-to-clear the way the phlebotomist
   * status pills do. It carries an explicit "All devices" option, so a
   * re-click that also cleared would be a second way to do one thing —
   * and the two disagree about what a re-click on "All devices" means.
   *
   * It does not reset the page either: `pageNumber` above derives that from
   * the filter the page was chosen under, so this path cannot drift from the
   * clamp's.
   */
  const handleDeviceChange = (value: string) => {
    setDeviceFilter(value);
  };

  const handlePageChange = (next: number) => {
    setPage({ scope: pageScope, number: next });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size as PageSize);
    setPage({ scope: pageScope, number: 1 });
  };

  /**
   * TODO(PROD-2007): opens the session details panel. Not in this task — the
   * row is clickable now so the affordance and the hit target ship with the
   * table rather than being retrofitted over finished rows.
   */
  const handleRowClick = () => {
    // Intentionally empty until the details panel lands.
  };

  /**
   * Opens the report in a new tab, exactly as the phlebotomist tab opens a
   * result document (`order-requests-tab.tsx`) and the Documents list opens a
   * file: `noopener,noreferrer`, and the browser renders the PDF or image
   * itself. Cross-origin by definition — `documentUrl` is a SAS-signed blob URL.
   *
   * It takes the REPORT, not the row, so it cannot be called for a session that
   * has no document to open: there is no `if (!url) return` branch here because
   * there is no way to reach it.
   */
  const handleViewReport = (report: SessionReport) => {
    window.open(report.documentUrl, '_blank', 'noopener,noreferrer');
  };

  /**
   * An attach landed. Two things happen, and the order is the point.
   *
   * 1. The name is remembered locally, so the row says something true
   *    immediately instead of sitting on the "no report" placeholder for as
   *    long as the round trip takes.
   * 2. The reports feed is re-read, because that — not `onSaved` — is where the
   *    document's URL comes from. `onSaved` hands back a bare id, and an id
   *    cannot be opened: the URL is signed per request, server-side.
   *
   * Until step 2 answers, the row shows the NAME and no eye. That is deliberate
   * and it is this screen's oldest rule: a control never appears unless it
   * works. An eye over an id we cannot resolve is the exact placeholder this
   * ticket exists to delete.
   */
  const handleReportSaved = (row: TechnicianSessionRow) => {
    setSeededReportNames((prev) => ({
      ...prev,
      [row.id]: row.profileName ?? SEEDED_REPORT_NAME,
    }));
    refetchReports();
  };

  const showCount = state === 'ready' || state === 'no-sessions';

  /**
   * The day, for the picker and for the copy. `parseLocalISODate` cannot fail
   * for a `day` the hook accepted, but the fallback keeps this total rather
   * than asserting — a header is not the place to throw.
   */
  const dayDate = parseLocalISODate(day) ?? new Date();
  const dayLong = formatLongDate(dayDate);

  /**
   * DECISION — THE ADVANCE BUTTONS ARE ABSENT OFF TODAY (PROD-2408).
   *
   * The pipeline is Booked → Arrived → Checked in → Completed and every step
   * is a statement about the present: the patient is in the clinic, the
   * patient is on the machine, the run has finished. None of those can be
   * asserted about last Thursday, and none can be asserted about next
   * Tuesday. Marking a three-day-old session "arrived" does not record
   * history, it writes today's timestamped claim onto a session that is over.
   *
   * ABSENT, not disabled — this screen's standing doctrine, the same call as
   * the missing cancel button. A greyed-out "Mark arrived" on Thursday's rows
   * teaches the technician that the screen is broken; nothing there teaches
   * them that Thursday is finished.
   *
   * BOTH DIRECTIONS, deliberately. Suppressing only the past would leave
   * tomorrow's sessions advanceable, which is the more dangerous half: a
   * mis-click on a future row is indistinguishable from real work and puts a
   * patient who has not arrived into the arrived state.
   *
   * WHAT STAYS on other days: the paperclip and the eye. Neither is a
   * pipeline transition — attaching yesterday's printout is ordinary catch-up
   * work, and looking at a finished report is most of the reason to go back a
   * day at all. Suppressing those would make the date picker pointless.
   */
  const canAdvance = isToday;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Body — the only scrolling region; the list is one card inset by the page padding
          (the list-table standard, src/index.css), its title line carrying the header.
          `overflow: clip` on the card instead of the standard's `hidden`: a clipped card is
          not a scroll container, so the sticky header keeps sticking to this scroll region. `w-fit min-w-full`: a table wider than the
          tab grows the card with it, so the region scrolls sideways instead of clipping columns.
          `state` maps 1:1 onto what renders inside the card. */}
      <div className="flex-1 min-h-0 overflow-auto scrollbar-hide px-[var(--page-pad)] pt-[var(--page-header-gap)] pb-[var(--page-pad)]">
        <section className="list-table w-fit min-w-full">
          {/* Header — the design's `.tbar` (artifact "Technician Screen Options",
              section 01): the title, the day the list covers, and Filters pushed to
              the right. Nothing else. */}
          <div className="list-table-title flex-wrap">
            <div>
              <div className="flex items-baseline gap-2">
                <h2>
                  Device Sessions
                </h2>
                {/* The design's `Today ▾` (artifact section 01), and now a real
                    one — PROD-2408. It shipped as a bare LABEL because
                    `useTechnicianSessions` read its window off the clock and
                    took no date, so a chevron would have opened nothing and
                    this screen's oldest rule is that a control never appears
                    unless it works. The hook takes a date now, so the chevron
                    is earned.

                    It is the SAME control the scheduling sub-toolbar draws —
                    `CalendarDateControl`, lifted out of `calendar-sub-toolbar
                    .tsx` for this. A second popover with its own month grid
                    and its own "Today" link would be a second date idiom in
                    one product.

                    The trigger reads "Today" only while the day IS today; on
                    any other day it reads the date, because a header that says
                    "Today" over Thursday's rows is the whole defect this
                    ticket is about. */}
                <CalendarDateControl
                  value={dayDate}
                  onChange={onDayChange}
                  label={isToday ? 'Today' : dayLong}
                  triggerClassName="text-xs font-medium text-text-muted"
                />
              </div>
              {/* Gated on `state`, NEVER on `rows.length`. A count derived from the
                  array alone reads "0 total" for the whole load — the exact defect
                  the Devices tab badge shipped (PROD-2364). */}
              {showCount ? (
                <p className="text-[length:var(--type-label-size)] font-normal text-text-secondary">
                  {totalRecords} total
                </p>
              ) : null}
            </div>

            {/* One machine is not a choice — and neither is a filter over a table
                that is not there. It renders ONLY in `'ready'`: in
                `'sessions-error'` and `'no-sessions'` there is nothing for it to
                narrow, so it would be a live control with no effect sitting above
                an error or an empty state.

                WHY THIS IS A MENU AND NOT A ROW OF PILLS (PROD-2406,
                2026-09-21). It shipped
                as a `Segmented` carrying one option per device, which the design
                never had and which a 57-device practice renders as a 57-pill
                strip across the top of the screen — wider than the table under
                it, and a header that takes more reading than the work does. The
                CAPABILITY is unchanged and deliberately so: same options, same
                sentinel, same `effectiveFilter` clamp behind it. Only the
                presentation is bounded now, which is the one thing a registry
                that grows without limit can break. */}
            {state === 'ready' && devices.length > 1 ? (
              <DeviceFilterMenu
                devices={devices}
                value={effectiveFilter}
                onChange={handleDeviceChange}
              />
            ) : null}
          </div>
        {state === 'loading' ? (
          <LoadingTable />
        ) : state === 'no-practice' ? (
          // No retry. The registry was never asked and never will be while the
          // user has no practice, so a Retry button would be a control that
          // provably does nothing — the spinner this state replaced already
          // made that mistake by implication.
          <WorklistEmpty
            heading="No practice selected"
            body="Choose a practice to see today's device sessions."
          />
        ) : state === 'devices-error' ? (
          <ErrorState
            heading="Couldn't load your devices"
            body="Try again. If the problem persists, contact support."
            onRetry={onRetry}
          />
        ) : state === 'no-devices' ? (
          // Explains, does not blame: this is a configuration gap for the
          // administrator, not a mistake the technician made.
          <WorklistEmpty
            heading="No device assigned"
            body="Ask your practice administrator to assign a device. Your sessions appear here once one is."
          />
        ) : state === 'sessions-error' ? (
          <ErrorState
            // Names the day it failed to load, because the technician can now
            // be looking at one they navigated to — "today's sessions" over a
            // failed fetch for 18 September is a second wrong fact stacked on
            // the first.
            heading={
              isToday
                ? "Couldn't load today's sessions"
                : `Couldn't load sessions for ${dayLong}`
            }
            body="Your devices loaded, but their sessions didn't. Try again."
            onRetry={onRetry}
          />
        ) : state === 'no-sessions' ? (
          // Names the machines, so this reads visibly differently from
          // "No device assigned" — same emptiness, opposite cause, opposite
          // fix. Do not let the pair converge on generic copy.
          //
          // And names the DAY (PROD-2408). "No sessions booked today" was
          // true of the only day this screen could show and is false on every
          // other one — an empty table plus a sentence about today is exactly
          // how a technician concludes the picker did nothing.
          <WorklistEmpty
            heading={
              isToday
                ? 'No sessions booked today'
                : `No sessions booked on ${dayLong}`
            }
            body={`${formatDeviceList(devices.map((d) => d.name))} ${
              devices.length === 1 ? 'is' : 'are'
            } set up. Nothing is scheduled.`}
          />
        ) : pagedRows.length === 0 ? (
          // Not one of the six: the feed is ready but the DEVICE FILTER
          // excludes everything. A state-level empty here would claim the day
          // is empty when it is only this machine's day that is.
          <WorklistEmpty
            heading="No sessions on this device"
            // "the rest of the day", not "the rest of today" — the sentence
            // is true whichever day the picker is on, so it needs no date and
            // cannot go stale against one.
            body="Switch to All devices to see the rest of the day."
          />
        ) : (
          <SessionsTable
            rows={pagedRows}
            advancingIds={advancingIds}
            canAdvance={canAdvance}
            reports={reports}
            seededReportNames={seededReportNames}
            onRowClick={handleRowClick}
            onAdvance={onAdvance}
            onAttach={setAttachTarget}
            onView={handleViewReport}
          />
        )}
        </section>
      </div>

      {/* Footer — only once there is a page of real rows to describe. */}
      {state === 'ready' ? (
        <ListPaginationFooter
          pageNumber={safePage}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          totalRecords={totalRecords}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      ) : null}

      {/* Attach report — the shared Documents drawer, narrowed to the two
          session-report types and seeded with the product. The panel opened
          from a row that already knows the patient, the device, the product
          and the session time, so it re-asks for none of them: re-asking is
          how a file ends up filed against a session it does not describe.

          The type is the one thing it DOES ask, because it is the one thing
          the row cannot answer: the fleet is mixed, and only the technician
          in front of the machine knows whether this session produced a
          radiology report or a procedure record. */}
      {/* Still gated on `patientId`, even though `SessionActions` now refuses
          to hand over a patient-less row: this is the guard that makes the
          drawer's own contract true (it cannot file against nothing), and it
          must not depend on a caller remembering. */}
      {attachTarget?.patientId ? (
        <NewFilePopup
          open
          onOpenChange={(open) => {
            if (!open) setAttachTarget(null);
          }}
          patientId={attachTarget.patientId}
          // PROD-2374 — THE write half of the session↔report link. `row.id`
          // IS the appointment id (`DayListRow.id` is `appointment.id`, and
          // the status mutation on this same screen posts it as
          // `appointmentId`), and it is the same id `useSessionReports` asks
          // the server about. Drop this and the document saves fine, the
          // column stays NULL, and the read can never match it again — the
          // exact defect this ticket exists to close, and one that is
          // invisible until a reload.
          appointmentId={attachTarget.id}
          // PROD-2374 (Andrew, 2026-09-19) — the OTHER half of that same
          // agreement, and the reason it is a shared constant rather than a
          // literal: `useSessionReports` sends this very list as the read's
          // `fileType` filter. A type this drawer can save but the read does
          // not ask for is a report that saves cleanly and is never found
          // again — no error, no log, invisible until a reload. Change the
          // constant, never this line.
          allowedTypes={SESSION_REPORT_TYPES}
          // PROD-2374 — the chrome, which the type restriction used to drag in
          // with it. While this drawer was `lockedType={Procedure}` it also got
          // the compact attach-one-file layout for free: drop zone first,
          // Facility only, wider sheet. Widening the picker to two types
          // dropped that layout silently, because one prop was doing two jobs.
          // It is its own prop now, so the worklist asks for both explicitly —
          // a two-type picker AND the attach-one-report presentation — and
          // neither can take the other away again.
          attachFileLayout
          defaultName={attachTarget.profileName}
          // The document id `onSaved` returns is deliberately unused: it cannot
          // open anything. The URL is signed per request and only the reports
          // feed can produce one, so the save triggers a re-read instead.
          onSaved={() => handleReportSaved(attachTarget)}
        />
      ) : null}
    </div>
  );
}

// ── Device filter ─────────────────────────────────────────

/**
 * The design's `Filters` affordance (artifact section 01, `.filters`), holding
 * the one filter this screen has: which machine.
 *
 * WHY A MENU. The registry is not a small, fixed set — the practice this was
 * built against carries 57 active devices — so any control that draws one
 * element per device grows without bound across the header. A disclosure does
 * not: the trigger is one button wide whether the practice has two machines or
 * two hundred, and the list inside it scrolls.
 *
 * WHY NOT THE ORDER REQUESTS FILTER SHEET. The phlebotomist tab this screen
 * mirrors opens a `Sheet` because it has five fields (patient, MRN, status,
 * from, to) and a draft/Apply contract to go with them. There is ONE field
 * here, and a slide-over that covers the worklist to ask a single question the
 * technician answers in one click is heavier than the question. Same button,
 * same icon, same right-aligned position, same active-count badge — so it
 * reads as the product's Filters and not as a bespoke control — with a popover
 * behind it instead of a sheet. If a second filter is ever added here, this is
 * the point at which it should become that sheet.
 *
 * NO DRAFT, NO APPLY. One field applied immediately, because there is nothing
 * to stage: a draft only earns its Apply when several fields have to change
 * together. The menu stays open after a pick so a mis-click is one more click
 * to undo rather than a reopen.
 *
 * WHAT THIS DOES NOT OWN: the clamp. `value` is the caller's `effectiveFilter`
 * — already reconciled against the live registry — and this component never
 * compares the two. It renders what it is given and reports what was clicked.
 */
function DeviceFilterMenu({
  devices,
  value,
  onChange,
}: {
  devices: readonly CalendarDevice[];
  value: string;
  onChange: (value: string) => void;
}) {
  const options = [
    { value: ALL_DEVICES, label: 'All devices' },
    ...devices.map((d) => ({ value: d.id, label: d.name })),
  ];
  // The badge counts APPLIED filters, which here is one or none — "All
  // devices" is the absence of a filter, not a filter whose value is "all".
  const activeCount = value === ALL_DEVICES ? 0 : 1;

  return (
    <Popover>
      <PopoverTrigger
        render={<Button size="sm" variant="outline" aria-label="Filters" />}
      >
        <SlidersHorizontal className="w-4 h-4 mr-2" aria-hidden />
        Filters
        {activeCount > 0 ? (
          <span className="ml-2 inline-flex items-center justify-center rounded-full bg-brand-primary text-white text-xs font-bold w-4 h-4">
            {activeCount}
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div
          role="radiogroup"
          aria-label="Device"
          // Bounded, and scrolls past the bound. 57 devices is the real case;
          // an unbounded list would put the far end of the registry below the
          // fold of the window with no way to reach it.
          className="max-h-72 overflow-y-auto py-1"
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onChange(opt.value)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                  'hover:bg-grey-100 focus:outline-none focus-visible:bg-grey-100',
                  isSelected
                    ? 'font-semibold text-brand-primary'
                    : 'text-text-primary',
                )}
              >
                {/* Always laid out, only sometimes drawn — so the labels line
                    up down the list instead of shifting by a tick's width. */}
                <Check
                  className={cn('h-4 w-4 shrink-0', !isSelected && 'invisible')}
                  aria-hidden
                />
                <span className="truncate" title={opt.label}>
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Table ────────────────────────────────────────────────────────────────

function SessionsTable({
  rows,
  advancingIds,
  canAdvance,
  reports,
  seededReportNames,
  onRowClick,
  onAdvance,
  onAttach,
  onView,
}: {
  rows: TechnicianSessionRow[];
  advancingIds: ReadonlySet<string>;
  /** False off today — no row draws an advance button. See `canAdvance`. */
  canAdvance: boolean;
  /** `appointmentId` → the persisted report. Empty while loading OR on failure. */
  reports: ReadonlyMap<string, SessionReport>;
  /** Session id → the name of a report attached on this page, pending its URL. */
  seededReportNames: Record<string, string>;
  onRowClick: (row: TechnicianSessionRow) => void;
  onAdvance: (row: TechnicianSessionRow, step: SessionStatusStep) => void;
  onAttach: (row: TechnicianSessionRow) => void;
  onView: (report: SessionReport) => void;
}) {
  return (
    <table className="w-full">
      <thead className="sticky top-0 z-10">
        <tr>
          <Th>Session time</Th>
          <Th>Patient name</Th>
          <Th>MRN</Th>
          <Th>Device</Th>
          <Th>Product</Th>
          <Th>Duration</Th>
          {/* NOT "Booked by" (the design page's label), and RE-CONFIRMED as
              such in PROD-2406 (2026-09-21), when the built screen was compared back to
              design and the rename read as drift.

              It is not drift, it is a missing field. "Booked by" means the
              person who raised the request — the phlebotomist-parity column
              that drove the whole option-01 choice — and no read this screen
              makes carries one. `POST /api/Appointment/ResourceList` answers
              with `ListAppointmentResponse`, whose participants are the
              patient, the provider and the device; `createdBy` exists only on
              the WRITE shapes (`CreateAppointmentRequest`,
              `DeviceBookingRequest`), never on the way back. There is no
              `createdByName` anywhere in the scheduling types.

              So the choice is between a column that lies and a column that is
              honest about a different fact. This is the second. The value is
              `CalendarDevice.technicianName` — the technician assigned to the
              MACHINE — and the header says so.

              TO ACTUALLY SHIP "Booked by": the appointment read has to return
              the booking's author (id + display name), the mapper has to carry
              it onto `DayListRow`, and then this header and the cell below
              change together. It is a backend change first; do not fake it
              from the device's technician, and do not relabel this column
              without the field. */}
          <Th>Technician</Th>
          <Th>Status</Th>
          <th
            aria-label="Actions"
            className="w-48 text-left"
          >
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          // `row.id` — never a `?? Math.random()` fallback. It is the
          // appointment's own id, carried straight through the mapper, and a
          // random key remounts the row on every render, which drops focus and
          // re-fires the drawer's effects. (The hook drops block-availability
          // entries and rows with no DEVICE id; it does not inspect `id`, so
          // do not read this as a guarantee that one is present.)
          <SessionRow
            key={row.id}
            row={row}
            busy={advancingIds.has(row.id)}
            canAdvance={canAdvance}
            report={reports.get(row.id)}
            seededReportName={seededReportNames[row.id]}
            onRowClick={onRowClick}
            onAdvance={onAdvance}
            onAttach={onAttach}
            onView={onView}
          />
        ))}
      </tbody>
    </table>
  );
}

function SessionRow({
  row,
  busy,
  canAdvance,
  report,
  seededReportName,
  onRowClick,
  onAdvance,
  onAttach,
  onView,
}: {
  row: TechnicianSessionRow;
  busy: boolean;
  canAdvance: boolean;
  report: SessionReport | undefined;
  seededReportName: string | undefined;
  onRowClick: (row: TechnicianSessionRow) => void;
  onAdvance: (row: TechnicianSessionRow, step: SessionStatusStep) => void;
  onAttach: (row: TechnicianSessionRow) => void;
  onView: (report: SessionReport) => void;
}) {
  return (
    <tr
      onClick={() => onRowClick(row)}
      className="hover:bg-grey-100/60 cursor-pointer"
    >
      <Td>{row.timeRange}</Td>
      <Td className="font-semibold text-text-primary">{row.patientName}</Td>
      <Td className="text-text-muted">{row.patientMrn ?? '—'}</Td>
      <Td>{row.deviceName}</Td>
      <Td title={row.profileName}>{row.profileName ?? '—'}</Td>
      {/* `undefined` is UNKNOWN, not zero — the hook refuses to clamp an
          unreadable window to 0 precisely so this cell can say "we don't
          know" with the same em-dash every other optional cell uses. A real
          zero-length window is `0` and still prints "0 min". */}
      <Td className="text-text-muted">
        {row.durationMinutes === undefined ? '—' : `${row.durationMinutes} min`}
      </Td>
      <Td className="text-text-muted">{row.deviceTechnicianName ?? '—'}</Td>
      <Td>
        <SessionStatus row={row} />
      </Td>
      {/* Fixed width (matching the `<th>`) so the placeholder row and the
          two-control row occupy the same footprint — the column must not jump
          as sessions advance down the page. */}
      <td className="w-48 px-3 py-2">
        <SessionActions
          row={row}
          busy={busy}
          canAdvance={canAdvance}
          report={report}
          seededReportName={seededReportName}
          onAdvance={onAdvance}
          onAttach={onAttach}
          onView={onView}
        />
      </td>
    </tr>
  );
}

/**
 * The status cell — the badge, except when the badge would be guessing.
 *
 * `StatusBadge` deliberately folds a status it cannot read into the neutral
 * `booked` bucket, and its documented safety valve is that "the caller still
 * shows its raw label, so a wire regression stays visible in QA". That valve
 * is defeated on a row whose status is missing altogether: the mapper coerces
 * it to `statusDisplay: ''`, and the badge's `label || STATUS_LABELS[status]`
 * — a `||` precisely so an empty API display cannot render a blank pill — then
 * substitutes the canonical **"Booked"**. The technician reads a blue Booked
 * session offering no "Mark arrived" and no reason why, which is the badge
 * asserting a status the session does not have.
 *
 * So this screen spots the fallback itself: `booked` on the colour axis while
 * the wire code is not `Booked` means unrecognised, and unrecognised says so.
 * Quiet on purpose — grey, no icon, no alarm; this is a rare wire regression,
 * not an error state the technician can act on — with the raw code in the
 * title so it still reaches whoever is triaging.
 *
 * Fixed HERE and not upstream: `StatusBadge` and `calendar-mapping` are shared
 * by many surfaces and both behaviours are deliberate. What is local to this
 * screen is the consequence — an action column that goes silent at the same
 * time, leaving the pill as the only thing the row says about itself.
 */
function SessionStatus({ row }: { row: TechnicianSessionRow }) {
  if (row.status === 'booked' && !isBookedSessionStatus(row.statusCode)) {
    return (
      <BridgeStatusChip
        semantic="neutral"
        title={
          row.statusCode
            ? `Unrecognised status "${row.statusCode}" — this screen cannot read it.`
            : 'This session arrived with no status.'
        }
        className="whitespace-nowrap"
      >
        Unknown
      </BridgeStatusChip>
    );
  }
  return (
    <AppointmentStatusChip status={row.status} label={row.statusDisplay} />
  );
}

// ── Actions ──────────────────────────────────────────────────────────────

/**
 * One control, one step — plus the paperclip, which follows a different rule.
 *
 * The ADVANCE button is derived entirely from `nextSessionStep`; the pipeline
 * is never re-derived in this file, and a status this file cannot read yields
 * no button rather than a guessed one.
 *
 * The PAPERCLIP is not on the pipeline at all. It rides on the two statuses
 * that can own a report:
 *
 *   - the row being worked right now (its next step ENDS the session, i.e.
 *     `Fulfilled`) — the report is produced while the machine is running;
 *   - the row that has ALREADY ended well (`isCompletedSessionStatus`) — see
 *     the decision recorded below.
 *
 * It is absent everywhere else: a Booked or Arrived session has produced
 * nothing yet, and a NoShow / Cancelled / EnteredInError session never ran, so
 * there is no printout that could belong to it. An unreadable status falls
 * through to the placeholder — the write-path safety case, unchanged.
 *
 * DECIDED 2026-09-18 (Andrew), closing the `TODO(PROD-2007): UNRESOLVED` that
 * stood here: **a completed session can still be given a report.**
 *
 * THE DESIGN SAYS OTHERWISE, AND THE DESIGN IS SUPERSEDED HERE. Section 05 of
 * the signed-off artifact ("Technician Screen Options", decided 17 Sep) draws
 * the attach control on the CHECKED IN row only and calls the completed row
 * "read-only — the file is shown, not re-uploadable". That was re-examined on
 * 2026-09-21 against this screen (PROD-2406) and RE-AFFIRMED as overruled: "for the upload
 * we will do as agreed that we can upload even after completion" (Andrew).
 * This is the one point on which the built screen deliberately departs from
 * the artifact. Anyone comparing the two will land here first, so: it is a
 * recorded decision, not drift. Do not "restore" the read-only rule.
 * A
 * technician who finishes the scan, marks the session complete and walks back
 * from the machine with the printout minutes later must be able to file it.
 * The old branch dropped the paperclip the instant the pipeline ended, which
 * read as a rule ("reports are attached before the session closes") but was
 * only ever an unanswered question. It is answered now: the affordance stays.
 *
 * The same decision left "Mark completed" UNBLOCKED on a report existing —
 * explicitly no gate, no confirm and no warning, to be revisited once the
 * report read-path lands. See the note on `canAttach` below.
 *
 * This changes the paperclip ONLY. `nextSessionStep` stays forward-only and a
 * completed session still has no step: nothing here reopens a session.
 *
 * THE REPORT COLUMN (PROD-2374). Once there is no step left — the session is
 * terminal or unreadable — the report side of the cell takes over, alongside
 * whatever paperclip the rule above allows. Three cases, in this order:
 *
 *   · a fetched report (it HAS a URL) → its name as a chip, plus the eye;
 *   · a report attached on this page whose URL has not arrived yet → the
 *     same chip, and NO eye;
 *   · anything else → nothing from this side; with no paperclip either, the
 *     placeholder.
 *
 * **The eye is gated on a URL, never on a boolean.** That is what makes
 * "reports are still loading", "the report fetch failed" and "this session has
 * no report" collapse to one placeholder with no branch of their own: there is
 * no state in which an eye can render without something for it to open. The
 * old TODO placeholder — an eye wired to an empty handler — is the defect that
 * shape exists to prevent (PROD-2374).
 *
 * **One glyph, one job.** The paperclip attaches. The eye views, and only
 * after the session is done — before that the row is still being worked.
 *
 * PROD-2438 — AND THE NAME IS NOT A GLYPH. It shipped as a second button wired
 * to the same `onView`, so a completed row offered two controls that did one
 * thing; Andrew, 21 Sep: "the name of the file and the eye are doing the
 * same". The name is now a `ReportChip` — a bordered label, semantically inert
 * (see that component). This restores §01/§06 of the signed-off artifact,
 * which draws the completed row as *chip plus eye* and says of the eye: "the
 * eye is view — the session and its report". Singular. Do not re-link the name.
 */
function SessionActions({
  row,
  busy,
  canAdvance,
  report,
  seededReportName,
  onAdvance,
  onAttach,
  onView,
}: {
  row: TechnicianSessionRow;
  busy: boolean;
  /**
   * False when the worklist is showing a day that is not today (PROD-2408).
   * The advance button is then ABSENT — see the decision recorded at
   * `canAdvance` in the body. The paperclip and the eye are unaffected.
   */
  canAdvance: boolean;
  report: SessionReport | undefined;
  seededReportName: string | undefined;
  onAdvance: (row: TechnicianSessionRow, step: SessionStatusStep) => void;
  onAttach: (row: TechnicianSessionRow) => void;
  onView: (report: SessionReport) => void;
}) {
  const step = nextSessionStep(row.statusCode);

  // Where the paperclip rides — see the header. `step.code === 'Fulfilled'` is
  // the row about to finish; `isCompletedSessionStatus` is the row that
  // already has. Deliberately NOT `isTerminalSessionStatus`: that also covers
  // NoShow and Cancelled, sessions that never ran and so have nothing to file.
  //
  // "Mark completed" is deliberately NOT blocked on a report existing. A
  // device that produces no file would otherwise strand its sessions open
  // forever — and now that a completed row keeps its paperclip, a technician
  // who marks complete early has lost nothing anyway. If a report is mandatory
  // for some device types, that is a rule on the PRODUCT, enforced where
  // products are configured — not a disabled button here. Do not "fix" this by
  // gating the button on `attached` (Andrew, 18 Sep: leave it unblocked,
  // revisit once the report read-path lands).
  const canAttach =
    step?.code === 'Fulfilled' || isCompletedSessionStatus(row.statusCode);

  /**
   * …but only a row that names its patient can actually file anything. The
   * drawer is opened AGAINST a patient id (`NewFilePopup` files the document
   * on a patient) and `DayListRow.patientId` is optional — a participant list
   * that comes back without an id leaves the row patient-less. The paperclip
   * was gated on status alone, so such a row offered an enabled control that
   * opened nothing and logged nothing: a dead control, which is the one thing
   * an affordance may never be.
   *
   * DISABLED, not absent — deliberately the opposite call to the missing
   * cancel button at the top of this file, and for the opposite reason. Cancel
   * is absent because the technician may NEVER have it, so greying it out only
   * sends them looking for someone who does. Attaching a report IS this
   * screen's job; it is THIS ROW that cannot do it, and hiding the control
   * would read as "reports don't belong on this session" and leave a
   * technician holding a chamber printout with nowhere to put it. The title
   * says which of the two it is, and names the fix (the session has no patient
   * record — front desk work, not the technician's).
   *
   * Untouched by the 18 Sep decision, and it applies to the completed row on
   * exactly the same terms: a completed session with no patient id shows the
   * disabled control and its explanation, never a working one.
   */
  const attachable = canAttach && Boolean(row.patientId);

  const attachButton = canAttach ? (
    <SessionIconButton
      title={attachable ? 'Attach report' : ATTACH_NEEDS_PATIENT}
      icon={<Paperclip className="size-4" aria-hidden />}
      disabled={busy || !attachable}
      onClick={stopped(() => onAttach(row))}
    />
  ) : null;

  /**
   * The step this row will actually OFFER, as opposed to the one the pipeline
   * says it has. They differ only off today, and the order matters: `step` is
   * read first and `canAttach` above is derived from THAT, so a Checked-in
   * row on Thursday keeps its paperclip while losing its "Mark completed".
   */
  const offeredStep = canAdvance ? step : null;

  if (!offeredStep) {
    // Terminal, unreadable, or a day the pipeline cannot be driven from.
    // Nothing to advance; what is left is the paperclip (the row being worked
    // or a completed one) and the report column. Neither → the placeholder,
    // which is still what a NoShow, a Cancelled and an unrecognised status
    // with no report get.
    if (!canAttach && !report && !seededReportName) return <ActionPlaceholder />;
    return (
      <div className="flex items-center gap-1">
        {attachButton}
        {report ? (
          <>
            <ReportChip name={report.name} />
            {/* The ONLY way to open the document (PROD-2438). Its accessible
                name carries the file, because the name beside it is no longer
                a link a screen-reader user can hear: "View report" alone told
                them an eye existed, not which of the day's files it opens. */}
            <SessionIconButton
              title="View report"
              label={`View report: ${report.name}`}
              icon={<Eye className="size-4" aria-hidden />}
              onClick={stopped(() => onView(report))}
            />
          </>
        ) : seededReportName ? (
          // Attached moments ago, URL not back yet. The SAME chip, and no eye:
          // a label reads identically whether or not there is something behind
          // it, which is the point — the column does not jump, and nothing
          // claims to be openable before it is. Deliberately not a DISABLED
          // control either, which on this screen would tell the technician the
          // power exists and send them looking for someone who has it.
          <ReportChip name={seededReportName} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {attachButton}
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={stopped(() => onAdvance(row, offeredStep))}
      >
        {offeredStep.label}
      </Button>
    </div>
  );
}

/** Same 8-high footprint as a button, so the column does not jump. */
function ActionPlaceholder() {
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center h-8 px-2 text-xs text-text-muted"
    >
      —
    </span>
  );
}

/**
 * The report's name, as a LABEL (PROD-2438).
 *
 * Semantically inert on purpose, and that is the whole component: a bare
 * `<span>` with no role, no `tabIndex`, no handler and no `cursor-pointer`.
 * It is announced as text and it is not in the tab order. Deleting the
 * `onClick` from the old `<button>` would NOT have been enough — a button
 * with no handler is still announced as a button, still focusable, and
 * silently does nothing when pressed, which is a worse control than the
 * duplicated one it replaced. So the ELEMENT changes, not just the prop.
 *
 * The shape is §01's `.chip`: bordered, grey ground, a file glyph and the
 * filename in mono, truncated. It reads as an attachment rather than as
 * something to press — the visual half of "the eye is the action".
 *
 * `title` carries the FULL name. Truncation is intended (device report names
 * run long — "HYPERBARIC OXYGEN THERAPY…"), so the untruncated string has to
 * stay recoverable on hover; it is the one thing the chip owes a reader that
 * the ellipsis takes away.
 *
 * Height matches `SessionIconButton` and `ActionPlaceholder` (h-8) so the
 * actions column does not jump between a row with a report and one without.
 */
function ReportChip({ name }: { name: string }) {
  return (
    <span
      title={name}
      className="inline-flex h-8 max-w-[224px] items-center gap-2 rounded-md border border-[var(--surface-line)] bg-grey-100 px-2"
    >
      <FileGlyph className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
      <span className="truncate font-mono text-size-xs text-text-primary">
        {name}
      </span>
    </span>
  );
}

function SessionIconButton({
  title,
  label,
  icon,
  disabled,
  onClick,
}: {
  /** The tooltip — terse, and what the action IS. */
  title: string;
  /**
   * The accessible name, when it should say more than the tooltip. Defaults
   * to `title`. Used by the eye, which names the document it opens
   * (PROD-2438) while its tooltip stays short enough to read at a glance.
   */
  label?: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  // The tooltip sits on a span so the explanation still shows when the button is disabled.
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex" />}>
        <button
          type="button"
          aria-label={label ?? title}
          disabled={disabled}
          onClick={onClick}
          className={`${ROW_ICON_BUTTON} text-text-secondary`}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}

/** Row click opens the session; a control inside it must not also. */
function stopped(fn: () => void) {
  return (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };
}

// ── States ───────────────────────────────────────────────────────────────

function LoadingTable() {
  return (
    <div className="p-6 space-y-3">
      <Skeleton className="h-5 w-1/3" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function ErrorState({
  heading,
  body,
  onRetry,
}: {
  heading: string;
  body: string;
  onRetry: () => void;
}) {
  return (
    <div className="p-12 text-center">
      <AlertCircle className="h-8 w-8 text-[var(--status-bad)] mx-auto" aria-hidden />
      <p className="mt-2 text-sm font-semibold text-[var(--status-bad)]">{heading}</p>
      <p className="mt-1 [font:var(--type-body)] text-[var(--ink-meta)]">{body}</p>
      <Button size="sm" variant="outline" className="mt-4" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/**
 * Not the Signal DS `EmptyState`, and deliberately not named like it either —
 * `scripts/check-ds-shadows.mjs` ratchets on new local declarations that
 * collide with a DS export name. The DS molecule takes a REQUIRED `icon` and
 * always draws it as a 48px blue disc above a 16px `<h3>`. The four empties on
 * this screen are icon-less at 14px on purpose: the one state here that earns a
 * glyph is `ErrorState` above, and the contrast is the point — a technician
 * scanning this pane should be able to tell "something broke" from "nothing is
 * booked" before reading a word. Four icon discs flatten that, and there is no
 * honest icon for "no device assigned". Wearing the DS name while diverging
 * from the DS shape is the drift the guard exists to stop, so this one takes a
 * local name instead of joining the allowlist.
 */
function WorklistEmpty({ heading, body }: { heading: string; body: string }) {
  return (
    <div className="p-12 text-center">
      <p className="text-sm font-semibold text-text-primary">{heading}</p>
      <p className="mt-1 [font:var(--type-body)] text-[var(--ink-meta)]">{body}</p>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left">
      {children}
    </th>
  );
}

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td
      title={title}
      className={cn(
        'text-text-primary align-middle',
        className,
      )}
    >
      {children}
    </td>
  );
}

/**
 * How many machines the empty state will NAME before it starts counting them.
 *
 * Three, because the point of naming them is recognition — "yes, those are my
 * machines, nothing is booked on them" — and recognition does not survive a
 * list. Past three the sentence stops being read and becomes a wall.
 */
const EMPTY_STATE_NAMED_DEVICES = 3;

/**
 * "A", "A and B", "A, B and C", "A, B, C and 54 others" — the empty state
 * names the machines, up to a point (PROD-2404).
 *
 * THE BOUND IS THE FIX. This was an unbounded join, which is fine for the
 * two-device practice it was written against and is a 57-name run-on
 * paragraph in the practice it actually shipped to. Same root as the header's
 * 57 pills (PROD-2406): both drew one element per registry entry, and the registry is not
 * a small set. Both are bounded now — the header by a disclosure, this by a
 * count — and neither can grow with the fleet again.
 *
 * NOTE FOR WHOEVER PICKS UP THE REST OF PROD-2404: the number this sentence
 * reports is the ACTIVE REGISTRY, and the registry is not the set of machines
 * that can carry work. The same practice has 57 active devices and 5 with
 * schedules / appointment profiles behind them; the other 52 can never appear
 * on this worklist because nothing can be booked on them. So "57 devices are
 * set up. Nothing is scheduled." is true and still misleading — the honest
 * sentence counts the schedulable ones. That is a change to what
 * `useCalendarDevices` returns (or a second, narrower feed), not to this
 * helper, so it is named here rather than guessed at.
 */
function formatDeviceList(names: string[]): string {
  if (names.length === 0) return 'No devices';
  if (names.length === 1) return names[0];
  if (names.length <= EMPTY_STATE_NAMED_DEVICES) {
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  const shown = names.slice(0, EMPTY_STATE_NAMED_DEVICES);
  const rest = names.length - EMPTY_STATE_NAMED_DEVICES;
  return `${shown.join(', ')} and ${rest} ${rest === 1 ? 'other' : 'others'}`;
}
